import React, { useEffect } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  Stack,
  Chip,
  Divider,
} from "@mui/material";
import { GlassPanel, ModernTextField } from "./styledComponents";

const Paso6HablameDeTi = ({
  formValues,
  onChange,
  dataNutriologoSubdominio,
  errors,
}) => {
  // ... (mantener el resto del código existente)

  const getSafeLabel = (value, fallback = "") => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "number") return value.toString();
    if (typeof value === "object") {
      return (
        value.nombre ||
        value.label ||
        value.titulo ||
        value.clave ||
        value._id ||
        fallback
      );
    }
    return fallback;
  };

  const getOptionValue = (option) => {
    if (option === null || option === undefined) return "";
    if (typeof option === "string" || typeof option === "number") {
      return option.toString();
    }
    if (typeof option === "object") {
      const candidate =
        option.clave || option.value || option.nombre || option._id;
      return candidate ? candidate.toString() : "";
    }
    return "";
  };

  const historiaClinica =
    dataNutriologoSubdominio &&
    dataNutriologoSubdominio.historiaClinicas &&
    dataNutriologoSubdominio.historiaClinicas.length > 0
      ? dataNutriologoSubdominio.historiaClinicas[0]
      : null;

  const cumpleDependencia = (dependencia) => {
    // (función cumpleDependencia sin cambios)
    const {
      pregunta: parentName,
      respuesta,
      requiredAnswers,
      minCount = 0,
    } = dependencia;
    const parentAnswer = formValues[parentName];

    if (respuesta !== undefined) {
      return parentAnswer === respuesta;
    }

    if (!parentAnswer) return false;

    if (minCount > 0) {
      if (Array.isArray(parentAnswer) && parentAnswer.length >= minCount) {
        return true;
      }
      if (!Array.isArray(parentAnswer) && 1 >= minCount) {
        return true;
      }
    }

    if (Array.isArray(requiredAnswers) && requiredAnswers.length > 0) {
      if (Array.isArray(parentAnswer)) {
        return requiredAnswers.every((ans) => parentAnswer.includes(ans));
      }
      return requiredAnswers.includes(parentAnswer);
    }

    return false;
  };

  // Efecto para limpiar respuestas de preguntas ocultas
  useEffect(() => {
    if (!historiaClinica) return;
    historiaClinica.preguntas.forEach((pregunta) => {
      if (
        pregunta.dependencias &&
        !cumpleDependencia(pregunta.dependencias) &&
        formValues[pregunta.nombre] !== undefined &&
        formValues[pregunta.nombre] !== "" &&
        formValues[pregunta.nombre].length !== 0
      ) {
        // Determinar el valor a limpiar según el tipo de pregunta
        let valorLimpio = "";
        if (pregunta.tipo === "CHECK") {
          valorLimpio = [];
        }
        // Actualiza el valor usando onChange
        onChange(pregunta.nombre, valorLimpio);
      }
    });
  }, [formValues, historiaClinica]);
  // Se dispara cuando cambian formValues o historiaClinica

  if (!historiaClinica) {
    return (
      <GlassPanel>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontWeight: "bold", color: "primary.main" }}
        >
          Por el momento no hay una historia clínica
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Puedes finalizar el registro.
        </Typography>
      </GlassPanel>
    );
  }

  return (
    <GlassPanel>
      <Stack spacing={2}>
        <Box>
          <Typography variant="overline" sx={{ letterSpacing: 3 }}>
            Paso 5
          </Typography>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            {historiaClinica.tituloHistoria}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comparte los detalles que ayudarán a personalizar tus estrategias de
            salud.
          </Typography>
          <Stack direction="row" spacing={1} mt={1}>
            {["Hábitos", "Salud", "Estilo de vida"].map((tag) => (
              <Chip key={tag} label={tag} size="small" variant="outlined" />
            ))}
          </Stack>
        </Box>
        <Divider />

        {historiaClinica.preguntas &&
          historiaClinica.preguntas.map((pregunta) => {
            if (
              pregunta.dependencias &&
              !cumpleDependencia(pregunta.dependencias)
            ) {
              return null;
            }

            return (
              <Box key={pregunta._id} sx={{ mt: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", marginBottom: 1 }}
                >
                  {getSafeLabel(pregunta.nombre)}
                </Typography>

                {pregunta.tipo === "TEXT" && (
                  <ModernTextField
                    fullWidth
                    variant="outlined"
                    placeholder="Escribe tu respuesta aquí"
                    value={formValues[pregunta.nombre] || ""}
                    onChange={(e) => onChange(pregunta.nombre, e.target.value)}
                    error={Boolean(errors && errors[pregunta.nombre])}
                    helperText={errors && errors[pregunta.nombre]}
                  />
                )}

                {pregunta.tipo === "RADIO" && (
                  <FormControl
                    component="fieldset"
                    sx={{ marginTop: 1 }}
                    error={Boolean(errors && errors[pregunta.nombre])}
                  >
                    <FormLabel component="legend">
                      Selecciona una opción:
                    </FormLabel>
                    <RadioGroup
                      row
                      value={formValues[pregunta.nombre] || ""}
                      onChange={(e) =>
                        onChange(pregunta.nombre, e.target.value)
                      }
                    >
                      {pregunta.opciones.map((opcion, i) => {
                        const optionValue = getOptionValue(opcion);
                        const optionLabel = getSafeLabel(opcion, optionValue);
                        return (
                          <FormControlLabel
                            key={i}
                            value={optionValue}
                            control={<Radio />}
                            label={optionLabel}
                          />
                        );
                      })}
                    </RadioGroup>
                    {errors && errors[pregunta.nombre] && (
                      <Typography color="error">
                        {errors[pregunta.nombre]}
                      </Typography>
                    )}
                  </FormControl>
                )}

                {pregunta.tipo === "CHECK" && (
                  <FormControl
                    component="fieldset"
                    sx={{ marginTop: 1 }}
                    error={Boolean(errors && errors[pregunta.nombre])}
                  >
                    <FormLabel component="legend">
                      Selecciona una o más opciones:
                    </FormLabel>
                    <FormGroup row>
                      {pregunta.opciones.map((opcion, i) => {
                        const optionValue = getOptionValue(opcion);
                        const optionLabel = getSafeLabel(opcion, optionValue);
                        return (
                          <FormControlLabel
                            key={i}
                            control={
                              <Checkbox
                                checked={
                                  Array.isArray(formValues[pregunta.nombre]) &&
                                  formValues[pregunta.nombre].includes(
                                    optionValue
                                  )
                                }
                                onChange={() => {
                                  const current = Array.isArray(
                                    formValues[pregunta.nombre]
                                  )
                                    ? [...formValues[pregunta.nombre]]
                                    : [];
                                  const updated = current.includes(optionValue)
                                    ? current.filter((x) => x !== optionValue)
                                    : [...current, optionValue];
                                  onChange(pregunta.nombre, updated);
                                }}
                              />
                            }
                            label={optionLabel}
                          />
                        );
                      })}
                    </FormGroup>
                    {errors && errors[pregunta.nombre] && (
                      <Typography color="error">
                        {errors[pregunta.nombre]}
                      </Typography>
                    )}
                  </FormControl>
                )}
              </Box>
            );
          })}
      </Stack>
    </GlassPanel>
  );
};

export default Paso6HablameDeTi;
